import {SitemapContent} from "@buildwithflux/search";
import {Routes} from "@buildwithflux/url";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {useParams} from "react-router";
import type {MetaArgs, MetaDescriptor} from "react-router";

import {useDocumentTitle} from "../hooks/useDocumentTitle";
import {APP_DESCRIPTION, siteMetaDescriptors, toCanonicalUrl} from "../seo";

export function meta({params}: MetaArgs): MetaDescriptor[] {
    const categoryKey = params.category?.toLowerCase() ?? "";
    const categoryData = SitemapContent[categoryKey];
    if (!categoryData) {
        return [{title: "Category not found"}];
    }

    const absoluteUrl = toCanonicalUrl(`/sitemap/${categoryKey}`);
    return [
        {title: `${categoryData.pageMetaTitle ?? categoryData.name} on Flux`},
        {name: "description", content: categoryData.description ?? APP_DESCRIPTION},
        {tagName: "link", rel: "canonical", href: absoluteUrl},
        {tagName: "link", rel: "alternate", type: "text/markdown", href: `${absoluteUrl}.md`},
        {property: "og:title", content: `Flux Sitemap/${categoryData.name}`},
        {property: "og:description", content: APP_DESCRIPTION},
        {property: "og:type", content: "website"},
        {property: "og:url", content: absoluteUrl},
        {name: "twitter:title", content: `Flux Sitemap/${categoryData.name}`},
        {name: "twitter:description", content: APP_DESCRIPTION},
        ...siteMetaDescriptors(),
        {
            "script:ld+json": {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                    {"@type": "ListItem", position: 1, name: "Home", item: "https://flux.ai"},
                    {"@type": "ListItem", position: 2, name: "Sitemap", item: toCanonicalUrl("/sitemap")},
                    {"@type": "ListItem", position: 3, name: categoryData.name, item: absoluteUrl},
                ],
            },
        },
    ];
}

export default function CategoryPage() {
    const {category} = useParams<{category: string}>();
    const categoryKey = category?.toLowerCase() ?? "";
    const categoryData = SitemapContent[categoryKey];

    useDocumentTitle(
        categoryData ? `${categoryData.pageMetaTitle ?? categoryData.name} on Flux` : "Category not found",
    );

    if (!categoryData) {
        return (
            <Container maxWidth="md" sx={{py: 4}}>
                <Typography variant="h4" component="h1">
                    Category not found
                </Typography>
            </Container>
        );
    }

    return (
        <>
            <Container maxWidth="md">
                <Grid container>
                    <Grid size={12}>
                        <Breadcrumbs aria-label="Breadcrumb" sx={{mb: 2}}>
                            <Link color="inherit" href={Routes.SITEMAP}>
                                Sitemap
                            </Link>
                            <Typography color="text.primary">{categoryData.name}</Typography>
                        </Breadcrumbs>

                        <Typography
                            color="text.primary"
                            variant="h2"
                            component="h1"
                            sx={{wordBreak: "break-word", mb: 2}}
                        >
                            {categoryData.name}
                        </Typography>

                        {categoryData.description && (
                            <Typography color="text.secondary" variant="body1" sx={{mb: 3}}>
                                {categoryData.description}
                            </Typography>
                        )}

                        <List aria-label="Subcategories">
                            {Object.entries(categoryData.subcategories).map(([subKey, sub]) => {
                                // Sitemap thumbs are complete content-addressed CDN
                                // URLs. The markup below handles sizing.
                                const thumbUrl = sub.thumb || undefined;

                                return (
                                    <Paper key={subKey} square component="li" sx={{mt: 1, p: 2, minHeight: "80px"}}>
                                        <Box sx={{display: "flex", justifyContent: "flex-start"}}>
                                            {thumbUrl && (
                                                <Box sx={{mr: 1, flexShrink: 0}}>
                                                    <Link href={`${Routes.SITEMAP}/${categoryKey}/${subKey}`}>
                                                        <Box
                                                            component="img"
                                                            src={thumbUrl}
                                                            alt={sub.name}
                                                            draggable={false}
                                                            sx={{width: 124, height: 124, objectFit: "contain"}}
                                                        />
                                                    </Link>
                                                </Box>
                                            )}
                                            <Box sx={{flex: 1, minWidth: 0}}>
                                                <Link
                                                    href={`${Routes.SITEMAP}/${categoryKey}/${subKey}`}
                                                    color="text.primary"
                                                    underline="none"
                                                >
                                                    <Typography color="text.primary" variant="h6" component="h2">
                                                        {sub.name}
                                                    </Typography>
                                                </Link>

                                                {sub.description && (
                                                    <Typography color="text.secondary" variant="body2">
                                                        {sub.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </List>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
